import type { ProbeInfo, StreamState } from '@shared/protocol';
import { fmtDuration } from '../lib/meterMath';
import { Panel } from './Panel';

const kbps = (b: number | null) => (b ? `${Math.round(b / 1000)} kb/s` : 'not stated');

function frameRate(r: string | null): string {
  if (!r) return 'unknown';
  const [n, d] = r.split('/').map(Number);
  if (!d) return r;
  const v = n / d;
  return Number.isInteger(v) ? `${v} fps` : `${v.toFixed(3)} fps (${r})`;
}

export function StreamInfo({ probe, state }: { probe: ProbeInfo | null; state: StreamState | null }) {
  const audio = probe?.audio[0];
  const uptime = state?.status === 'live' ? (Date.now() - state.since) / 1000 : null;
  return (
    <Panel title="Stream info">
      {!probe ? (
        <p className="muted">Stream details appear once the source has been probed.</p>
      ) : (
        <div className="info-sections">
          <section>
            <h4>Audio (monitored track)</h4>
            {audio ? (
              <dl className="kv">
                <dt>Codec</dt>
                <dd>
                  {audio.codec}
                  {audio.profile ? ` (${audio.profile})` : ''}
                </dd>
                <dt>Sample rate</dt>
                <dd>{(audio.sampleRate / 1000).toFixed(1)} kHz</dd>
                <dt>Channels</dt>
                <dd>
                  {audio.channels}
                  {audio.channelLayout ? ` (${audio.channelLayout})` : ''}
                </dd>
                <dt>Bitrate</dt>
                <dd>{kbps(audio.bitRate)}</dd>
                {audio.language && (
                  <>
                    <dt>Language</dt>
                    <dd>{audio.language}</dd>
                  </>
                )}
                {probe.audio.length > 1 && (
                  <>
                    <dt>Other tracks</dt>
                    <dd>{probe.audio.length - 1} (selectable in a later stage)</dd>
                  </>
                )}
              </dl>
            ) : (
              <p className="muted">No audio track found.</p>
            )}
          </section>
          <section>
            <h4>Video{probe.video.length > 1 ? ` (${probe.video.length} renditions)` : ''}</h4>
            {probe.video.length === 0 && <p className="muted">No video track found.</p>}
            {probe.video.map((v) => (
              <dl className="kv" key={v.index}>
                <dt>Format</dt>
                <dd>
                  {v.width}x{v.height}, {frameRate(v.frameRate)}
                </dd>
                <dt>Codec</dt>
                <dd>
                  {v.codec}
                  {v.profile ? ` (${v.profile})` : ''}
                </dd>
                <dt>Scan (flagged)</dt>
                <dd>{v.fieldOrder ? (v.fieldOrder === 'progressive' ? 'progressive' : `interlaced, ${v.fieldOrder}`) : 'not flagged'}</dd>
                <dt>Aspect</dt>
                <dd>
                  SAR {v.sar ?? '?'}, DAR {v.dar ?? '?'}
                </dd>
                <dt>Colour</dt>
                <dd>
                  {[v.pixFmt, v.colorSpace, v.colorRange && `${v.colorRange} range`, v.colorPrimaries].filter(Boolean).join(', ') || 'not flagged'}
                </dd>
                <dt>Bitrate</dt>
                <dd>{kbps(v.bitRate)}</dd>
              </dl>
            ))}
          </section>
          <section>
            <h4>Connection</h4>
            <dl className="kv">
              <dt>Container</dt>
              <dd>{probe.formatName ?? 'unknown'}</dd>
              <dt>Type</dt>
              <dd>{probe.duration === null ? 'Live' : `On demand (${fmtDuration(probe.duration)})`}</dd>
              <dt>Uptime</dt>
              <dd className="num">{uptime === null ? '--' : fmtDuration(uptime)}</dd>
              <dt>Reconnects</dt>
              <dd className="num">{state?.restarts ?? 0}</dd>
              <dt>Last probed</dt>
              <dd>{new Date(probe.at).toLocaleTimeString('en-GB')}</dd>
            </dl>
          </section>
        </div>
      )}
    </Panel>
  );
}
